import React, { useState } from 'react'
import { useI18n } from '../../hooks/useI18n'

interface StockUpdateModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (newStock: number) => void
  onTakeWithoutUpdating?: () => void
  medicationName: string
  currentStock: number
}

const StockUpdateModal: React.FC<StockUpdateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onTakeWithoutUpdating,
  medicationName,
  currentStock
}) => {
  const { t } = useI18n()
  const [newStock, setNewStock] = useState(10) // Default to 10 units

  if (!isOpen) return null

  const handleConfirm = () => {
    onConfirm(newStock)
    onClose()
  }

  const handleTakeWithoutUpdating = () => {
    if (onTakeWithoutUpdating) {
      onTakeWithoutUpdating()
    } else {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('stock-update-required')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-yellow-800 dark:text-yellow-200 font-medium">
                {t('low-stock-warning')}
              </span>
            </div>
          </div>

          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {t('stock-update-message', { medication: medicationName, currentStock: currentStock })}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('new-stock-amount')}
            </label>
            <input
              type="number"
              value={newStock}
              onChange={(e) => setNewStock(Math.max(0, parseInt(e.target.value) || 0))}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
              placeholder={t('enter-new-stock')}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('stock-update-hint')}
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleTakeWithoutUpdating}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {t('take-without-updating')}
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            {t('update-stock-and-take')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default StockUpdateModal